import { createRouter, createWebHistory } from 'vue-router'
import EntryBoard from '@/views/EntryBoard.vue'
import ShellLayout from '@/layouts/index.vue'
import SystemMonitor from '@/views/SystemMonitor.vue'
import BusinessLog from '@/views/BusinessLog.vue'
import TraceMonitoring from '@/views/TraceMonitoring.vue'
import VehicleMonitoring from '@/views/VehicleMonitoring.vue'
import VehicleExtIdMonitoring from '@/views/VehicleExtIdMonitoring.vue'
import Login from '@/views/Login.vue'
import MbcPmsInformation from '@/views/MbcPmsInformation.vue'
import QueueMonitoring from '@/views/QueueMonitoring.vue'
import SalesInformation from '@/views/SalesInformation.vue'
import TestDataAdmin from '@/views/TestDataAdmin.vue'
import ApiConfig from '@/views/ApiConfig.vue'
import UserManagement from '@/views/UserManagement.vue'
import RoleManagement from '@/views/RoleManagement.vue'
import MenuManagement from '@/views/MenuManagement.vue'
import VinTenantIdConfiguration from '@/views/VinTenantIdConfiguration.vue'
import DataInsight from '@/views/DataInsight.vue'
import Unauthorized from '@/views/error/403.vue'
import { isSSOPending, clearSSOPending } from '@/utils/login'
import NProgress from 'nprogress'
import 'nprogress/nprogress.css'
import store from '@/store'


/* static routes that have no permission requirement */
const staticRoutes = [
  { path: '/login', name: 'login', component: Login },
  { path: '/403', name: 'forbidden', component: Unauthorized },
  { path: '/404', name: 'not-found', component: Unauthorized } // TODO add 404 page if required
]

/* set default route */
const defaultRouteName='entry-board'
// const defaultRoute = { path: '', redirect: { name: defaultRouteName } }
const defaultRoute = { path: '', name: defaultRouteName, component: EntryBoard }

/* Navigate to default page - for use in navigation guards */
const navigateToDefaultPage = (next, replace = false) => {
  // Solution1(current): set a fixed default entry page as landing page
  // Solution2: dynamically determine default page based on user permission and route config, this requires additional logic to determine which page to navigate to based on routeConfigs and user permission
  next({ name: defaultRouteName, replace })
}

/* Navigate to default page - for use in components */
export const navigateToDefaultRoute = (routerInstance) => {
  return routerInstance.replace({ name: defaultRouteName })
}

/* route and component mapping, content of key is for backward compatibility, this could be updated after DB item update */
const routeComponentMap = {
  "entry": { path: 'entry', name: 'entry', component: EntryBoard },
  "user": { path: 'system-admin/users', name: 'user-management', component: UserManagement },
  "role": { path: 'system-admin/roles', name: 'role-management', component: RoleManagement },
  "menu": { path: 'system-admin/menus', name: 'menu-management', component: MenuManagement },
  "/systemMonitoring": { path: 'monitor', name: 'monitor', component: SystemMonitor },
  "/businessMonitoring": { path: 'business-log', name: 'business-log', component: BusinessLog },
  "/traceMonitoring": { path: 'trace-monitoring', name: 'trace-monitoring', component: TraceMonitoring },
  "/vehicleMonitoring": { path: 'vehicle-monitor', name: 'vehicle-monitor', component: VehicleMonitoring },
  "/vehicleExtMonitoring": { path: 'vehicle-extId-monitor', name: 'vehicle-extId-monitor', component: VehicleExtIdMonitoring },
  "/mbcPmsInformation": { path: 'mbc-pms', name: 'mbc-pms', component: MbcPmsInformation },
  "/queueMonitoring": { path: 'queue-monitor', name: 'queue-monitor', component: QueueMonitoring },
  "/salesInformation": { path: 'sales-info', name: 'sales-info', component: SalesInformation },
  "/testDataAdministration": { path: 'test-data-admin', name: 'test-data-admin', component: TestDataAdmin },
  "/apiConfiguration": { path: 'api-config', name: 'api-config', component: ApiConfig },
  "/vinTenantIdConfiguration": { path: 'vin-tenant-config', name: 'vin-tenant-config', component: VinTenantIdConfiguration },
  "/dataInsight": { path: 'data-insight', name: 'data-insight', component: DataInsight },
}

/* convert route config list to route list */
const convertConfigToRoutes = (routeConfigs) => {
  const routes = []
  routes.push(defaultRoute)

  if (!routeConfigs || routeConfigs.length === 0) {
    return routes
  }

  routeConfigs.forEach(routeConfig => {
    const route = routeComponentMap[routeConfig.path]
    if (route && !routes.some(existingRoute => existingRoute.name === route.name)) {
      routes.push(route)
    }
  })

  return routes
}

/* dynamically reload routes based on new route config list */
export function reloadRoutes(routeConfigs) {
  const newRoutes = convertConfigToRoutes(routeConfigs)

  // remove all existing routes except for static routes
  const staticRouteNames = staticRoutes.map(r => r.name)
  router.getRoutes().forEach(route => {
    // Skip routes without names and static routes
    if (!route.name || staticRouteNames.includes(route.name)) {
      return
    }
    // console.debug('[router] removing route:', route.name)
    router.removeRoute(route.name)
  })

  // add new routes
  const parentRoute = {
    path: '/',
    component: ShellLayout,
    children: newRoutes
  }
  router.addRoute(parentRoute)
  console.log('[router] reloaded routes:', newRoutes)
}

// keep original static routes for reference, TODO remove after dynamic routing is stable
const referenceRoutes = [
  { path: '/login', name: 'login', component: Login },
  { path: '/403', name: 'forbidden', component: Unauthorized },
  {
    path: '/',
    component: ShellLayout,
    children: [
      { path: '', redirect: { name: 'entry' } },
      // { path: 'entry', name: 'entry', component: EntryBoard },
      { path: 'monitor', name: 'monitor', component: SystemMonitor },
      { path: 'business-log', name: 'business-log', component: BusinessLog },
      { path: 'trace-monitoring', name: 'trace-monitoring', component: TraceMonitoring },
      { path: 'vehicle-monitor', name: 'vehicle-monitor', component: VehicleMonitoring },
      { path: 'vehicle-extId-monitor', name: 'vehicle-extId-monitor', component: VehicleExtIdMonitoring },
      { path: 'mbc-pms', name: 'mbc-pms', component: MbcPmsInformation },
      { path: 'queue-monitor', name: 'queue-monitor', component: QueueMonitoring },
      { path: 'sales-info', name: 'sales-info', component: SalesInformation },
      { path: 'test-data-admin', name: 'test-data-admin', component: TestDataAdmin },
      { path: 'api-config', name: 'api-config', component: ApiConfig },
      { path: 'system-admin/users', name: 'user-management', component: UserManagement },
      { path: 'system-admin/roles', name: 'role-management', component: RoleManagement },
      { path: 'system-admin/menus', name: 'menu-management', component: MenuManagement },
      { path: 'vin-tenant-config', name: 'vin-tenant-config', component: VinTenantIdConfiguration },
    ],
  },
]

/* routes that do not require authentication (except for login page) */
const publicRoutes = [
  '/403',
  '/404',
  '/cfes/check',
  '/oauth2/authorization/cloudidp',
  '/auth-redirect', // TODO remove if not used
  '/bind', // TODO remove if not used
  '/cfes/fodhandler/admin/login' // TODO remove if not used
]

/* load user info after authentication */
const loadUserData = async () => {
  try {
    if (!store.getters.username) {
      await store.dispatch('GetInfo')
      console.log('[guard] loaded user info:', store.getters.username)
    }
  } catch (err) {
    console.warn('[guard] Failed to load user data:', err)
  }
}

/* Session-based flags - reset on page refresh, persist during SPA navigation */
let routesLoadedInSession = false

/* Load dynamic routes after authentication */
const loadUserRoute = async () => {
  try {
    const routerConfigs = await store.dispatch('GetRouters')
    console.log('[guard] loaded user routers:', routerConfigs.data)
    await store.dispatch('GenerateRoutes')
    routesLoadedInSession = true
    console.log('[guard] Routes successfully loaded in session')
  } catch (error) {
    console.warn('[guard] Failed to load routes:', error)
    routesLoadedInSession = false
    throw error
  }
}

/* Validate if a route exists in the router */
const isValidRoute = (to) => {
  if (!to) return false

  // Check by route name
  if (to.name && router.hasRoute(to.name)) {
    return true
  }

  // Check by path matching
  const matchedRoute = router.getRoutes().find(route => {
    if (route.path === to.path) return true
    // Handle dynamic routes with params
    if (route.path.includes(':') && to.path.startsWith(route.path.split(':')[0])) {
      return true
    }
    return false
  })

  return !!matchedRoute
}

const router = createRouter({
  history: createWebHistory(),
  routes: staticRoutes
})

NProgress.configure({ showSpinner: false })

router.beforeEach(async (to, from, next) => {
  console.info('%c[guard] beforeEach:navigating', 'color: blue; ', {
    to: to.fullPath,
    from: from.fullPath,
    ssoPending: isSSOPending(),
    routesLoadedInSession
  })
  NProgress.start()

  // Handle public routes (no authentication required, not including login page)
  if (publicRoutes.includes(to.path)) {
    next()
    return
  }

  // Login page has special SSO handling and navigation
  if (to.path === '/login') {
    // Check if SSO is still pending after redirect back
    if (!isSSOPending()) {
      next()
      NProgress.done()
      // TODO if already authenticated, consider load router and redirecting to default page instead of allowing to stay on login page, but this depends on whether we want to allow user to re-login or switch account after already authenticated
      return
    }

    // When user is being redirect back to login page from IDP server, authentication state mush be fetched from backend
    console.log('[guard] SSO in pending state, user is being redirect back to login page')
    const authResult = await store.dispatch('RefreshPrincipal')
    console.log('[guard] login probe authentication:', authResult)

    // Always clear SSO pending state to prevent infinite loops
    clearSSOPending()

    if (authResult) {
      navigateToDefaultPage(next)
    } else {
      // If authenticate state is still false, then SSO is likely failed or cancelled
      console.error('[guard] SSO authentication failed or cancelled, staying on login page')
      next()
    }

    NProgress.done()
    return
  }

  // Check authentication (handles cache internally)
  let isAuthenticated = await store.dispatch('GetPrincipal')

  // Redirect to login page if trying to access pages requires authentication but not authenticated 
  console.log('[guard] authenticated:', isAuthenticated)
  if (!isAuthenticated) {
    next({ name: 'login' })
    NProgress.done()
    return
  }

  // Load user data for authenticated users
  await loadUserData()

  // Check if routes are loaded in current session
  const hasRoutes = router.getRoutes().some(route =>
    route.name && !['login', 'forbidden', 'not-found'].includes(route.name)
  )

  if (!routesLoadedInSession || !hasRoutes) {
    console.log('[guard] Loading user routes...')

    try {
      await loadUserRoute()

      // Validate target route exists after loading
      if (isValidRoute(to)) {
        console.log('[guard] Routes loaded, target route valid, re-navigating to:', to.fullPath)
        next({ ...to, replace: true })
      } else {
        console.warn('[guard] Target route not found after loading routes:', to.fullPath)
        navigateToDefaultPage(next, true)
      }
    } catch (error) {
      console.error('[guard] Route loading failed:', error)
      navigateToDefaultPage(next, true)
    }
  } else {
    // Routes already loaded in session, proceed normally
    console.log('[guard] Routes already loaded in session, proceeding to:', to.fullPath)

    // Validate route exists before proceeding
    if (!isValidRoute(to)) {
      console.warn('[guard] Target route not found:', to.fullPath)
      navigateToDefaultPage(next, true)
      return
    }

    next()
  }

})

router.afterEach(() => {
  NProgress.done()
})

router.onError((error, to, from) => {
  // Always stop progress bar on any error
  NProgress.done()

  // Handle specific error types
  switch (error.name) {
    case 'NavigationDuplicated':
      // Harmless - user navigated to same route, ignore
      return

    case 'NavigationAborted':
      // Navigation was aborted by another navigation, ignore
      return

    case 'NavigationCancelled':
      // Navigation was cancelled, ignore
      return

    case 'ChunkLoadError':
      // Code splitting chunk failed to load, reload page
      console.warn('[router] Chunk load error, reloading page')
      window.location.reload()
      return

    default:
      // Log all other errors for debugging
      console.error('[router] Navigation error:', {
        error: error,
        to: to?.path,
        from: from?.path,
        message: error?.message,
        stack: error?.stack
      })
  }

  // Handle redirect loops - aggressive recovery
  if (error?.message?.includes('redirect') || error?.message?.includes('loop')) {
    console.error('[router] Detected redirect loop, resetting state')
    routesLoadedInSession = false
    router.replace('/403').catch(() => {
      console.error('[router] Failed to navigate to safe route, reloading page')
      window.location.href = '/403'
    })
    return
  }

  // TODO show a toast notification here for user-friendly error for unexpected errors
  console.error('[router] Unexpected navigation error')
})

export default router
